import { useState } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { formatAtomicUnits } from "@/lib/x402";
import type { PaymentAccept } from "@/lib/x402";
import { useAcceptTokens } from "@/hooks/useAcceptTokens";

interface PaywallBannerProps {
  accepts: PaymentAccept[];
  onPay: (accept: PaymentAccept) => void;
  loading?: boolean;
}

export function PaywallBanner({ accepts, onPay, loading }: PaywallBannerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { tokens, isError } = useAcceptTokens(accepts);

  if (!accepts.length) return null;

  const selected = accepts[selectedIndex];
  const selectedToken = tokens?.[selectedIndex];
  const price = selectedToken
    ? `${formatAtomicUnits(selected.amount, selectedToken.decimals)} ${selectedToken.symbol}`
    : "";
  const hasMultiple = accepts.length > 1;

  return (
    <div className="bg-white rounded-2xl border border-beige/30 p-8 text-center my-8 shadow-sm">
      <div className="flex justify-center mb-4">
        <div className="p-3 rounded-full bg-orange/10">
          <Lock className="text-orange" size={24} />
        </div>
      </div>
      <h3 className="text-xl font-display font-bold mb-2">
        This article is paywalled
      </h3>
      <p className="text-grey font-body text-sm mb-6">
        {isError
          ? "Payment details could not be loaded. Please try again later."
          : `Pay ${price} to unlock the full article. Payment is instant and goes directly to the writer.`}
      </p>

      {hasMultiple && tokens && (
        <div className="flex justify-center gap-2 mb-4">
          {accepts.map((accept, i) => {
            const { symbol, decimals } = tokens[i];
            const amt = formatAtomicUnits(accept.amount, decimals);
            return (
              <button
                key={i}
                onClick={() => setSelectedIndex(i)}
                className={`px-4 py-2 rounded-lg text-sm font-body font-bold transition-colors ${
                  i === selectedIndex
                    ? "bg-orange text-white"
                    : "bg-beige/10 text-grey hover:bg-beige/20"
                }`}
              >
                {amt} {symbol}
              </button>
            );
          })}
        </div>
      )}

      <Button
        size="lg"
        onClick={() => onPay(selected)}
        loading={loading || (!selectedToken && !isError)}
        disabled={!selectedToken}
      >
        Pay {price}
      </Button>
    </div>
  );
}
